import { Stack, Typography } from '@mui/material';
import {
  GridCallbackDetails,
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { CopyToClipboard, CustomDataGrid, StackedTextCell } from '@retail/components';
import { useRowColorGrouping } from '@retail/hooks';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { IconButton, PricePerUnit } from '@retail/products/components';
import { getLLDate } from '@retail/utils';
import { EditButtonIcon, TrashIcon } from '@shared/custom-icons';
import { MutableRefObject, useMemo } from 'react';

const baseColumnProps: Partial<GridColDef<PriceSuggestionPrice>> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  priceSuggestionPrices: PriceSuggestionPrice[];
  isLoadingFetchPrices: boolean;
  apiRef: MutableRefObject<GridApiPremium | null>;
  checkedItems: PriceSuggestionPrice[];
  checkItems: (priceSuggestionPrices: PriceSuggestionPrice[]) => void;
  setItemToDelete: (priceSuggestionPrice: PriceSuggestionPrice) => void;
  setItemToEdit: (priceSuggestionPrice: PriceSuggestionPrice) => void;
}

const itemIdSelector = (priceSuggestionPrice: PriceSuggestionPrice) =>
  `${priceSuggestionPrice.mgSupplierItem.mgSupplierItemId}${priceSuggestionPrice.validTo ?? ''}`;

export function FixedPricesTable({
  priceSuggestionPrices,
  isLoadingFetchPrices,
  apiRef,
  checkedItems,
  checkItems,
  setItemToDelete,
  setItemToEdit,
}: Props) {
  const t = useCalculusTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const onRowsSelectionHandler = (
    selectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (details.reason === 'singleRowSelection') {
      checkItems(
        priceSuggestionPrices.filter((ps) => Array.from(selectionModel.ids).includes(itemIdSelector(ps)))
      );
    } else if (checkedItems.length !== priceSuggestionPrices.length) {
      checkItems(priceSuggestionPrices);
    } else {
      checkItems([]);
    }
  };

  const selectionModel: GridRowSelectionModel = {
    type: 'include',
    ids: new Set<GridRowId>(checkedItems.map((item) => itemIdSelector(item))),
  };

  const columns: GridColDef<PriceSuggestionPrice>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'mgItemName',
        headerName: t('calculus.itemsAffected.columns.itemName'),
        width: 280,
        valueGetter: (_, row) => row.mgSupplierItem?.primaryText,
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={row.mgSupplierItem?.primaryText}
            bottomLine={row.mgSupplierItem?.secondaryText}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'mgItem/-finfonr',
        headerName: 'mgItem number',
        renderHeader: () => (
          <Typography variant="subtitle1">
            {t('calculus.itemsAffected.columns.mgItemAndFinfo')}
          </Typography>
        ),
        width: 160,
        valueGetter: (_, row) => row.mgSupplierItem?.mgItemNumber,
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={
              <CopyToClipboard
                value={row.mgSupplierItem?.mgItemNumber}
                hasIconButtonPadding={false}
              >
                {row.mgSupplierItem?.mgItemNumber}
              </CopyToClipboard>
            }
            bottomLine={row.mgSupplierItem?.finfoNumber}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        headerName: 'supplier id',
        renderHeader: () => (
          <Typography variant="subtitle1">{t('calculus.level.supplier')}</Typography>
        ),
        width: 200,
        valueGetter: (_, row) => row.mgSupplierItem?.mgParticipantAccount.mgSupplierId,
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={row.mgSupplierItem?.mgParticipantAccount.name}
            bottomLine={row.mgSupplierItem?.mgParticipantAccount.mgSupplierId}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'price',
        headerName: 'sales price',
        renderHeader: () => (
          <Typography variant="subtitle1">{t('calculus.prices.salesPrice')}</Typography>
        ),
        width: 180,
        valueGetter: (_, row) => row.price,
        renderCell: ({ row }) => {
          const price = row.price;
          const unit = row.unit;
          return <PricePerUnit price={price} unit={unit.unit} description={unit.description} />;
        },
      },
      {
        ...baseColumnProps,
        field: 'validFrom',
        headerName: 'from date',
        renderHeader: () => <Typography variant="subtitle1">{t('calculus.activeFrom')}</Typography>,
        width: 200,
        valueGetter: (_, row) => row.validFrom,
        renderCell: ({ row }) => (
          <Typography variant="subtitle2" fontWeight={600}>
            {getLLDate(row.validFrom)}
          </Typography>
        ),
      },
      {
        ...baseColumnProps,
        field: 'validTo',
        headerName: 'to date',
        renderHeader: () => <Typography variant="subtitle1">{t('calculus.activeTo')}</Typography>,
        width: 200,
        valueGetter: (_, row) => row.validTo,
        renderCell: ({ row }) => (
          <Typography variant="subtitle2" fontWeight={600}>
            {getLLDate(row.validTo) ?? '-'}
          </Typography>
        ),
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        width: 100,
        renderCell: ({ row }) => (
          <Stack flexDirection="row" alignItems="center">
            <IconButton onClick={() => setItemToEdit(row)}>
              <EditButtonIcon />
            </IconButton>
            <IconButton onClick={() => setItemToDelete(row)}>
              <TrashIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [setItemToDelete, setItemToEdit, t]
  );

  return (
    <CustomDataGrid<PriceSuggestionPrice>
      rows={priceSuggestionPrices}
      columns={columns}
      checkboxSelection
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={onRowsSelectionHandler}
      getRowClassName={getRowColorForStripedTable}
      getRowId={(row) => itemIdSelector(row)}
      initialState={{
        pinnedColumns: {
          right: ['actions'],
        },
      }}
      disableRowSelectionOnClick
      disableColumnMenu
      loading={isLoadingFetchPrices}
      slotProps={{ toolbar: { csvOptions: { allColumns: true } } }}
      hideFooter
      apiRef={apiRef}
    />
  );
}
